import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import psl from 'psl'

import { SectionAmount }              from "../SectionAmount/SectionAmount"
import { SectionBackToLandingPage }   from "../SectionBackToLandingPage/SectionBackToLandingPage"
import { SectionBankDetails }         from "../SectionBankDetails/SectionBankDetails"
import { SectionBillingAddress }      from "../SectionBillingAddress/SectionBillingAddress"
import { SectionCardDetails }         from "../SectionCardDetails/SectionCardDetails"
import { SectionFieldGroup }          from "../SectionFieldGroup/SectionFieldGroup"
import { SectionGiftAid }             from "../SectionGiftAid/SectionGiftAid"
import { SectionInMemory }            from "../SectionInMemory/SectionInMemory"
import { SectionOrganisationDetails } from "../SectionOrganisationDetails/SectionOrganisationDetails"
import { SectionOptins }              from "../SectionOptins/SectionOptins"
import { SectionPersonalDetails }     from "../SectionPersonalDetails/SectionPersonalDetails"
import { SectionQuickPay }            from "../SectionQuickPay/SectionQuickPay";
import { SectionReasonForDonating }   from "../SectionReasonForDonating/SectionReasonForDonating"
import { SectionCustomFields }        from "../SectionCustomFields/SectionCustomFields"
import { SectionRecipientDetails }    from "../SectionRecipientDetails/SectionRecipientDetails"
import { SectionAddress }             from "../SectionAddress/SectionAddress"
import { SectionSpacer }              from "../SectionSpacer/SectionSpacer"

import { ProgressBar }                from "../../02-Molecules/ProgressBar/ProgressBar"
import { SponsorshipSideBar }         from "../../02-Molecules/SponsorshipSideBar/SponsorshipSideBar"

import { brainTreeCreate }            from "../../Utility/BrainTree"
import { LoadingScreen }              from "../../02-Molecules/LoadingScreen/LoadingScreen"
import { clientTokenGet, orderSend }  from "../../Utility/WoodGreenPaymentsApi"

import './FormStep.scss'
import {elementCheckVisibility, elementCheckVisuallyHidden, setDonationReminderCookie} from "../../Utility/Utility"
import {PaymentStateManager} from "../../Utility/PaymentStateManager"
import { SectionCoverDonationFee } from '../SectionCoverDonationFee/SectionCoverDonationFee'

const SectionTypes = {
  SectionAmount             : SectionAmount,
  SectionGiftAid            : SectionGiftAid,
  SectionBackToLandingPage  : SectionBackToLandingPage,
  SectionBankDetails        : SectionBankDetails,
  SectionBillingAddress     : SectionBillingAddress,
  SectionCardDetails        : SectionCardDetails,
  SectionFieldGroup         : SectionFieldGroup,
  SectionInMemory           : SectionInMemory,
  SectionOptins             : SectionOptins,
  SectionOrganisationDetails: SectionOrganisationDetails,
  SectionPersonalDetails    : SectionPersonalDetails,
  SectionQuickPay           : SectionQuickPay,
  SectionReasonForDonating  : SectionReasonForDonating,
  SectionCustomFields       : SectionCustomFields,
  SectionRecipientDetails   : SectionRecipientDetails,
  SectionAddress            : SectionAddress,
  SectionSpacer             : SectionSpacer,
  SectionCoverDonationFee   : SectionCoverDonationFee,
}

/**
 * Primary UI component for user interaction
 */
export const FormStep = ({ actions, includesSideBar, includesProgressBar, ctaText, loqateKey, canGoBack, currentStepNumber, totalSteps, giftaidRate, googleMerchantId, googleMerchantEnvironment, hasBraintree, isSubmitStep, onValuesBulkUpdate, paymentHasCompleted, sections, shouldScroll, shouldUpdateOrder, stateManager, staticText, onTrackCheckoutStep, stepAutoSubmit, validationHash, values, donationFeeRate, ...props }) => {
  const refs = sections.map(() => useRef())
  const [brainTreeInstances, brainTreeInstancesSet] = useState(null)
  const [processing, processingSet]                 = useState(false)
  const [canProgress, canProgressSet]               = useState(true)
  const [paymentSelected, selectPayment]            = useState(false)
  const [, stateRefresh]                            = useState(Date.now())

  /**
   * Return the BrainTree instances.
   *
   * @returns {{hostedFields: {}, threeDSecure: {}, dataCollector: {}, paypalCheckoutInstance: {}}}
   */
  const brainTreeInstancesGet = () => {
    return brainTreeInstances
  }

  /**
   * Get the CTA text for the given CTA.
   *
   * @param {{ctaText: {submit: string, next: string}}} cta
   *   The CTA schema.
   *
   * @returns {string}
   */
  const ctaButtonText = (cta) => {
    return isSubmitStep ? cta.ctaText.submit : cta.ctaText.next
  }

  /**
   * Carry out the next action.
   */
  const doNextAction = () => {
    props.onNextAction('forward')
  }


  /**
   * Handle the order update action.
   */
  const doOrderUpdateAction = () => {
    const promises = []

    refs.forEach(ref => {
      if (ref.current && ref.current['doAction']) {
        promises.push(ref.current['doAction']())
      }
    })

    Promise.all(promises)
      .then(responses => {
        responses.forEach(responseValues => {
          Object.keys(responseValues).forEach(key => stateManager.valueUpdate(key, responseValues[key]))
        })

        return orderSend(stateManager.valuesGet())
      })
      .then(response => {
        stateManager.valueUpdate('uuid',     response.uuid)
        stateManager.valueUpdate('order_id', response.order_id)
        props.onNextAction('forward')
      })
      .catch(error => {
        if (error) {
          refs.forEach(ref => {
            if (ref.current && ref.current['setError']) {
              ref.current['setError'](error.message)
            }
          })
        }

        stateManager.valueUpdate('payment_nonce',"")
        processingSet(false)
      })
  }

  /**
   * Handle the cart action.
   *
   * @param {string} action
   *   The action to perform
   * @param {number} cartItemIndex
   *   The index of the cart item to perform the action on.
   */
  const handleCartAction = (action, cartItemIndex) => {
    if (action === 'remove') {
      stateManager.orderRemoveItemAtIndex(cartItemIndex)
      sessionUpdateFromValues()

      if (stateManager.hasOrders()) {
        stateRefresh(Date.now())
      }
      else {
        props.onNavigateToLandingPage()
      }
    }
  }

  /**
   * Handle the child input change.
   *
   * @param {string} name
   *   The name of the state to update
   * @param {string|boolean|{}|number} value
   *   The value to update.
   */
  const childInputChange = (name, value) => {
    console.log(name, value)
    stateManager.valueUpdate(name, value)
    stateRefresh(Date.now())
    // if single step form, save input values on every input change
    // don't update values on the Reason for donation step
    if (totalSteps <= 2 && name !== 'payment_reason') {
      sessionUpdateSingleStepFromValues()
    }

    // Update donation reminder cookie with local storage values.
    // Invalidate cookies if the payment reason has changed.
    const userValues = JSON.parse(localStorage.getItem('sponsorshipData'));
    if (userValues) {
      setDonationReminderCookie(userValues, name === 'payment_reason')
    }
  }

  /**
   * Check if the form step is in a valid state.
   *
   * @returns {boolean}
   */
  const isValid = () => {
    return refs.reduce((isValid, ref) => {
      return ref.current && ref.current['isValid'] ? ref.current['isValid']() ? isValid : false : isValid
    }, true)
  }

  /**
   * Handle the next event.
   */
  const handlerDoNext = () => {
    if (isValid()) {
      processingSet(true)
      stepShouldUpdateOrder() ? doOrderUpdateAction() : doNextAction()
      sessionUpdateFromValues()
    }
    else {
      props.onValidate()
    }
  }

  /**
   * Handle the secondary action.
   */
  const handlerDoSecondaryAction = () => {
    if (actions.secondary.location) {
      if (isValid()) {
        sessionUpdateFromValues()
        window.location.href = actions.secondary.location
      }
      else {
        props.onValidate()
      }
    }
  }

  /**
   * Check if the step should update the order on the server.
   *
   * @returns {boolean}
   */
  const stepShouldUpdateOrder = () => {
    if (typeof shouldUpdateOrder !== 'undefined') {
      if (shouldUpdateOrder === false) {
        return false
      }
    }

    return true
  }

  /**
   * Update the stored session values.
   */
  const sessionUpdateFromValues = () => {
    const values = stateManager.valuesGet()

    if (values.order_items.length) {
      values.created = Date.now() / 1000

      localStorage.setItem('sponsorshipData', JSON.stringify(values))

      const orderedProducts = {};
      values.order_items.forEach(item => {
        const arrayKey = `${item.product_id}${item.is_gift ? '_gift' : ''}`;
        if (orderedProducts[arrayKey] === undefined) {
          orderedProducts[arrayKey] = {
            name: `${item.description}${item.is_gift ? ' gift' : ''}`,
            amount: 0,
            size: 0,
          };
        }
        orderedProducts[arrayKey].amount += item.amount;
        orderedProducts[arrayKey].size += 1;
      });

      // Set cookie
      const parsedDomain = psl.parse(window.location.hostname);
      const cookieString = JSON.stringify(orderedProducts);
      document.cookie = `orderItems=${cookieString};domain=${parsedDomain.domain};path=/;secure=true`
    }

  }

  /**
   * Update the stored session values for single step forms
   */
  const sessionUpdateSingleStepFromValues = () => {
    const values = stateManager.valuesGet()
    values.created = Date.now() / 1000
    // Don't save these values...
    values.pay_with_card = undefined

    if(values.payment_type !== wgFlowConfiguration.expectedValues.payment_type) {
      values.payment_type = wgFlowConfiguration.expectedValues.payment_type
    }

    // Set local storage values
    localStorage.setItem('sponsorshipData', JSON.stringify(values))
  }

  useEffect(() => {
    setTimeout(() => {
      const formError = document.body.querySelector('div[data-state="error"],.braintree-hosted-fields-invalid')
      if (formError) {
        formError.scrollIntoView()
      }
    }, 100)
  }, [validationHash])

  useEffect(() => {
    if (hasBraintree) {
      clientTokenGet()
        .then(clientToken => brainTreeCreate(clientToken, googleMerchantId))
        .then(instances => {
          brainTreeInstancesSet(instances)
        })
        .catch(() => {
          brainTreeInstancesSet(false)
        })
    }

    const step = document.querySelector('.o-form-step')

    if (step && shouldScroll) {
      step.scrollIntoView()
    }
  }, [])

  if (stepAutoSubmit) {
    useEffect(() => {
      if (stateManager.paymentHasCompleted()) {
        handlerDoNext()
      }
    }, [stateManager.paymentHasCompleted()])
  }

  /**
   * A string representing classes to be applied to container - columns created if sidebar included.
   *
   * @type {string}
   */
  const containerClasses = includesSideBar ? 'o-form-step__container o-form-step__container--sidebar' : 'o-form-step__container'

  /**
   * Get the current order index.
   *
   * @type {number}
   */
  const currentOrderIndex = stateManager.ordersGetCurrentIndex()

  /**
   * A boolean indicating if the step has a secondary action.
   *
   * @type {boolean}
   */
  const hasSecondaryAction = actions.secondary && elementCheckVisibility(actions.secondary, values, stateManager.orderItemAtIndexIsGift(currentOrderIndex))

  /**
   * A boolean indicating if max sponsorships has been reached.
   */
  const maxSponsorshipsLimitReached = actions.secondary && actions.secondary.conditions && actions.secondary.conditions.max_sponsorships && stateManager.orderItemsGet().length >= actions.secondary.conditions.max_sponsorships

  /**
   * Check if we have quick pay enabled.
   */
  let isQuickPay = false;

  return (
    <div className={containerClasses}>
      {includesProgressBar &&
        <div className='o-form-step__progress-mobile'>
        <ProgressBar
            numberOfSteps={totalSteps}
            currentStep={currentStepNumber + 1}
        />
        </div>
      }
      {includesSideBar &&
        <div className={'o-form-step__column-sidebar'}>
          <SponsorshipSideBar
            paymentType={stateManager.paymentTypeGet()}
            sponsorshipItems={stateManager.orderItemsGet()}
            onCartAction={handleCartAction}
            openByDefault={currentStepNumber < 1 ? true : false}
          />
        </div>
      }

      <div className={'o-form-step__column-content'}>
        <div className={'o-form-step o-wysiwyg'}>
          {includesProgressBar &&
            <div className='o-form-step__progress-desktop'>
              <ProgressBar
                numberOfSteps={totalSteps}
                currentStep={currentStepNumber + 1}
              />
            </div>
          }
          {sections.map((section, key) => {
            if (!isQuickPay && section.sectionId === 'quick_pay') {
              isQuickPay = true;
            }

            return (
              (typeof SectionTypes[section.type] !== 'undefined') && elementCheckVisibility(section, stateManager.valuesGet(), stateManager.orderItemAtIndexIsGift(currentOrderIndex)) ?
                React.createElement(SectionTypes[section.type], {
                  ...section,
                  onInputChange            : childInputChange,
                  ref                      : refs[key],
                  values                   : stateManager.valuesGet(),
                  staticText               : staticText,
                  key                      : key,
                  validationHash           : validationHash,
                  loqateKey                : loqateKey,
                  giftaidRate              : giftaidRate,
                  donationFeeRate          : donationFeeRate,
                  onSetProcessing          : processingSet,
                  onDoNext                 : handlerDoNext,
                  onCanProgressSet         : canProgressSet,
                  paymentHasCompleted      : stateManager.paymentHasCompleted(),
                  brainTreeIsInstantiated  : brainTreeInstances === null ? null : brainTreeInstances !== false,
                  brainTreeInstancesGet    : brainTreeInstancesGet,
                  googleMerchantEnvironment: googleMerchantEnvironment,
                  visuallyHidden           : elementCheckVisuallyHidden(section, stateManager.valuesGet()),
                  stateManager             : stateManager,
                  trackEvent               : onTrackCheckoutStep,
                  itemIndex                : currentOrderIndex,
                  onPaymentSelect          : selectPayment
                }) : null
            )
          })}

          <ul className={'o-form-step__actions'} data-has-secondary-action={hasSecondaryAction && !maxSponsorshipsLimitReached}>
            {hasSecondaryAction && !maxSponsorshipsLimitReached &&
              <li>
                <button className={'b-button-green-light-outline-large'} onClick={handlerDoSecondaryAction}>{ctaButtonText(actions.secondary)}</button>
              </li>
            }

            {canProgress &&
              <li className={`o-form-step__actions-action-primary ${
                isQuickPay ? `o-form-step__actions-action-primary--quick-pay ${
                  paymentSelected ? 'o-form-step__actions-action-primary--quick-pay--show' : 'o-form-step__actions-action-primary--quick-pay--hide'
                }`
                : ''}`
              }>
                <button className={'b-button-green-light-large'} onClick={handlerDoNext} >{ctaButtonText(actions.primary)}</button>
              </li>
            }

            {canGoBack &&
              <li className={'o-form-step__actions-action-back'}>
                <button onClick={() => props.onNextAction('back')} >{'Back'}</button>
              </li>
            }

          </ul>

          {processing && <LoadingScreen message={'Processing your details'} />}
        </div>
      </div>
    </div>
  )
}

/**
 * FormStep PropTypes
 */
FormStep.propTypes = {
  actions: PropTypes.shape({
    primary: PropTypes.shape({
      ctaText: PropTypes.shape({
        next  : PropTypes.string,
        submit: PropTypes.string
      })
    }),
    secondary: PropTypes.shape({
      ctaText:PropTypes.shape({
        next  : PropTypes.string,
        submit: PropTypes.string
      }),
      location  : PropTypes.string,
      conditions: PropTypes.object
    })
  }),
  ctaText: PropTypes.shape({
    next  : PropTypes.string,
    submit: PropTypes.string,
  }),
  canGoBack                : PropTypes.bool,
  giftaidRate              : PropTypes.number,
  googleMerchantId         : PropTypes.string,
  googleMerchantEnvironment: PropTypes.string,
  hasBraintree             : PropTypes.bool,
  loqateKey                : PropTypes.string,
  isSubmitStep             : PropTypes.bool,
  paymentHasCompleted      : PropTypes.bool,
  sections                 : PropTypes.array,
  shouldScroll             : PropTypes.bool,
  shouldUpdateOrder        : PropTypes.bool,
  onInputChange            : PropTypes.func,
  onNextAction             : PropTypes.func,
  onNavigateToLandingPage  : PropTypes.func,
  onTrackCheckoutStep      : PropTypes.func,
  onValidate               : PropTypes.func,
  onValuesBulkUpdate       : PropTypes.func,
  stepAutoSubmit           : PropTypes.bool,
  staticText               : PropTypes.object,
  totalSteps               : PropTypes.number,
  values                   : PropTypes.object,
  validationHash           : PropTypes.string,
  stateManager             : PropTypes.instanceOf(PaymentStateManager),
}
